import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { HOME_CONTENT_MOCK } from './data/home-content.mock';
import { HomeCuentaComponent, type HomeCuentaPerfil } from './components/home-cuenta/home-cuenta.component';
import { HomeCuentaCampaniaService } from './services/home-cuenta-campania.service';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Segmentacion PN/PJ del hero; propagado a `home-cuenta`. */
  heroPersonaType: HomeCuentaPerfil = 'natural';

  @ViewChild(HomeCuentaComponent)
  private homeCuenta?: HomeCuentaComponent;

  private readonly subs = new Subscription();
  private pendingPerfil: HomeCuentaPerfil | null = null;
  private pendingFragment: string | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly campania: HomeCuentaCampaniaService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    this.subs.add(
      this.route.queryParamMap.subscribe(() => this.syncFromUrl())
    );
    this.subs.add(
      this.route.fragment.subscribe(() => this.syncFromUrl())
    );
    this.subs.add(
      this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => this.syncFromUrl())
    );
  }

  ngAfterViewInit(): void {
    const campania = this.campania.peek();
    if (campania) {
      this.applyPerfil(campania.perfil);
    }
    this.syncFromUrl();
    this.flushPendingScroll();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onHeroPersonaChange(type: HomeCuentaPerfil): void {
    this.applyPerfil(type);
    this.scrollToFragment(HOME_CONTENT_MOCK.anchors.creaCuenta);
  }

  /**
   * La URL manda:
   * - `?perfil=natural|juridica` → form PN/PJ
   * - `#cualquier-id` → scroll si existe ese elemento en el DOM
   */
  private syncFromUrl(): void {
    const perfil = this.parsePerfil(this.route.snapshot.queryParamMap.get('perfil'));
    const fragment = this.route.snapshot.fragment;
    const id = this.route.snapshot.queryParamMap.get('id');
    const token = this.route.snapshot.queryParamMap.get('token');

    if (perfil) {
      this.applyPerfil(perfil);

      if (id && token) {
        this.campania.setParams({
          perfil,
          idCampania: id,
          token,
        });
        this.clearCampaniaQueryFromUrl();
        return;
      }
    }

    if (fragment) {
      this.pendingFragment = fragment;
      this.scrollToFragment(fragment);
    }
  }

  private applyPerfil(perfil: HomeCuentaPerfil): void {
    this.pendingPerfil = perfil;
    this.heroPersonaType = perfil;
    this.homeCuenta?.setPerfil(perfil);
    this.cdr.detectChanges();
  }

  private parsePerfil(raw: string | null | undefined): HomeCuentaPerfil | null {
    const perfilRaw = String(raw || '').toLowerCase();
    if (perfilRaw === 'juridica' || perfilRaw === 'persona-juridica') {
      return 'juridica';
    }
    if (perfilRaw === 'natural' || perfilRaw === 'persona-natural') {
      return 'natural';
    }
    return null;
  }

  private clearCampaniaQueryFromUrl(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
      fragment: HOME_CONTENT_MOCK.anchors.creaCuenta,
      replaceUrl: true,
    });
  }

  /** Scroll a cualquier ancla: el `id` del fragmento debe existir en la pagina. */
  private scrollToFragment(fragment: string): void {
    const target = document.getElementById(fragment);
    if (!target) {
      return;
    }
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  private flushPendingScroll(): void {
    if (this.pendingFragment) {
      this.scrollToFragment(this.pendingFragment);
      return;
    }
    if (this.pendingPerfil) {
      this.scrollToFragment(HOME_CONTENT_MOCK.anchors.creaCuenta);
    }
  }
}
